import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LifecycleBadge from '../components/common/LifecycleBadge';
import { opsApi } from '../api/services';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { Inbox, Loader2, CheckCircle2 } from 'lucide-react';

const CATEGORIES = [
  { key: null,                     label: 'All' },
  { key: 'cancellation',           label: 'Cancellations' },
  { key: 'hold',                   label: 'Holds' },
  { key: 'vendor_issue',           label: 'Vendor issues' },
  { key: 'customer_approval',      label: 'Customer approval' },
  { key: 'address_verification',   label: 'Address verify' },
  { key: 'data_incomplete',        label: 'Data incomplete' },
  { key: 'tracking',               label: 'Tracking' },
  { key: 'manual_review',          label: 'Manual review' },
  // Issue #26 (Kalpana) — not a handoff reason like the others: this one
  // filters to orders flagged "both original and reship delivered", so the
  // confirmation cases are findable without hiding the closed orders Ops uses.
  { key: 'reshipped',              label: 'Reshipped' },
];

const SUB_QUEUES = [
  { key: null,    label: 'Email + CC' },
  { key: 'email', label: 'Email only' },
  { key: 'cc',    label: 'CC only' },
];

export default function OpsQueue() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [subQueue, setSubQueue] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (category) params.category = category;
      if (subQueue) params.subQueue = subQueue;
      const res = await opsApi.queue(params);
      setRows(res.data.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, subQueue]);

  // Summary counts across categories, computed from full queue for the tab badges.
  const [counts, setCounts] = useState({});
  useEffect(() => {
    opsApi.queue({ subQueue: subQueue || undefined }).then((res) => {
      const c = {};
      for (const r of res.data.rows || []) {
        const cat = r._category || 'other';
        c[cat] = (c[cat] || 0) + 1;
      }
      // 'reshipped' is derived per row, not a reason category, so count it directly.
      c.reshipped = (res.data.rows || []).filter((r) => r.both_delivered_pending_confirm).length;
      c._total = (res.data.rows || []).length;
      setCounts(c);
    }).catch(() => { /* ignore */ });
  }, [subQueue]);

  return (
    <div>

      <div className="p-6">
        {/* Sub-queue toggle */}
        <div className="mb-4 flex items-center gap-2">
          <span className="text-xs text-gray-500 mr-2">Sub-queue:</span>
          {SUB_QUEUES.map((s) => (
            <button
              key={s.key || 'all'}
              onClick={() => setSubQueue(s.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                subQueue === s.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Category tabs */}
        <div className="mb-4 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => {
            const n = c.key ? (counts[c.key] || 0) : (counts._total || 0);
            return (
              <button
                key={c.key || 'all'}
                onClick={() => setCategory(c.key)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border ${
                  category === c.key
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>{c.label}</span>
                <span className="text-xs text-gray-500">({n})</span>
              </button>
            );
          })}
        </div>

        {/* Queue table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 flex items-center justify-center text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Inbox className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <div className="text-sm">No orders in this view.</div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Order</th>
                  <th className="text-left px-4 py-2.5">Customer</th>
                  <th className="text-left px-4 py-2.5">Reason</th>
                  <th className="text-left px-4 py-2.5">Stage</th>
                  <th className="text-left px-4 py-2.5">Queue</th>
                  <th className="text-left px-4 py-2.5">Waiting</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-mono text-xs">
                      <Link to={`/orders/${r.id}`} className="text-blue-600 hover:underline">
                        {r.order_id_external}
                      </Link>
                      {r.is_reship && (
                        <span className="ml-1 text-[10px] bg-purple-100 text-purple-700 px-1 rounded">reship</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">{r.customer_name}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-gray-700">{r.reason_label || r.current_owner_reason_code}</span>
                      {r.refund_payment_status && r.refund_payment_status !== 'none' && (
                        <div className="text-[11px] text-gray-500 mt-0.5">Refund: {r.refund_payment_status.replace(/_/g, ' ')}</div>
                      )}
                      {/* Issue #23 — flag rather than a pop-up, at Kalpana's
                          request: it stays visible until the order is reviewed. */}
                      {r.both_delivered_pending_confirm && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium bg-green-100 text-green-800 px-1.5 py-0.5 rounded">
                          <CheckCircle2 className="w-3 h-3" />
                          Both original and reship delivered — please confirm
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5"><LifecycleBadge stage={r.lifecycle_stage} ownerTeam={r.current_owner_team} hasReship={r.has_reship} /></td>
                    <td className="px-4 py-2.5 text-xs">
                      {r.ops_sub_queue === 'cc' && (
                        <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-800">CC</span>
                      )}
                      {r.ops_sub_queue === 'email' && (
                        <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Email</span>
                      )}
                      {(!r.ops_sub_queue || r.ops_sub_queue === 'none') && (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">
                      {r.current_owner_since
                        ? formatDistanceToNow(new Date(r.current_owner_since), { addSuffix: true })
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
